<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';
import ProjectPreview from "@/component/ProjectPreview.vue";
import ProjectPreviewModel from "@/models/ProjectPreviewModel";
import PikuTradeComponent from "@/component/project/PikuTradeComponent.vue";
import CGJComponent from "@/component/project/CGJComponent.vue";
import ReferundumComponent from "@/component/project/ReferundumComponent.vue";
import StageComponent from "@/component/project/StageComponent.vue";
import NDIComponent from "@/component/project/NDIComponent.vue";
import HomeLabComponent from "@/component/project/HomeLabComponent.vue";
import SAEInfraComponent from "@/component/project/SAEInfraComponent.vue";

@Component({
  components: {ProjectPreview},
})
export default class ProjectPreviewList extends Vue {
  private list: ProjectPreviewModel[] = [
    {pathToProject: "sae-infra", project: SAEInfraComponent},
    {pathToProject: "homelab", project: HomeLabComponent},
    {pathToProject: "ndi", project: NDIComponent},
    {pathToProject: "pikuTrade", project: PikuTradeComponent},
    {pathToProject: "cgj", project: CGJComponent},
    {pathToProject: "referendum", project: ReferundumComponent},
    {pathToProject: "stage", project: StageComponent}
  ];

  private suivant: ProjectPreviewModel = this.list[1];
  private courant: ProjectPreviewModel = this.list[0];
  private precedent: ProjectPreviewModel = this.list[this.list.length - 1];
  private index: number = 0;

  private isMobile: boolean = false; // Variable pour détecter les appareils mobiles

  mounted() {
    this.detectDevice();
    window.addEventListener("resize", this.detectDevice);
    if (!this.isMobile) {
      window.addEventListener("wheel", this.handleScroll, {passive: true});
      window.addEventListener("keydown", this.handleKeydown); // Ajout de l'écoute du clavier
    }
  }

  beforeDestroy() {
    window.removeEventListener("resize", this.detectDevice);
    if (!this.isMobile) {
      window.removeEventListener("wheel", this.handleScroll);
      window.removeEventListener("keydown", this.handleKeydown); // Suppression de l'écoute du clavier
    }
  }

  private detectDevice() {
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  private handleScroll(event: WheelEvent) {
    const delta = event.deltaX;

    // Réduire la sensibilité pour les trackpads
    if (Math.abs(delta) < 50) return;

    if (delta > 0) {
      this.scrollApres();
    } else {
      this.scrollAvant();
    }
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.key === "ArrowRight") {
      this.scrollApres();
    } else if (event.key === "ArrowLeft") {
      this.scrollAvant();
    }
  }

  private modulo(n: number, total: number): number {
    return ((n % total) + total) % total;
  }

  private scrollAvant() {
    this.index = this.modulo(this.index - 1, this.list.length);
    this.update();
  }

  private scrollApres() {
    this.index = this.modulo(this.index + 1, this.list.length);
    this.update();
  }

  private update(): void {
    this.suivant = this.list[this.modulo(this.index + 1, this.list.length)];
    this.courant = this.list[this.modulo(this.index, this.list.length)];
    this.precedent = this.list[this.modulo(this.index - 1, this.list.length)];
  }


  private getDotClass(dotIndex: number): string {
    if (dotIndex === this.index) {
      return 'dot active';
    } else if (dotIndex === this.modulo(this.index - 1, this.list.length) || dotIndex === this.modulo(this.index + 1, this.list.length)) {
      return 'dot adjacent';
    } else {
      return 'dot';
    }
  }

  private scrollToIndex(dotIndex: number) {
    this.index = dotIndex;
    this.update();
  }
}
</script>

<template>
  <div class="list-container">
    <!-- Boutons visibles uniquement sur mobile -->
    <div v-if="isMobile" class="nav-btn">
      <button @click="scrollAvant" class="mdi mdi-menu-up btn"></button>
      <button @click="scrollApres" class="mdi mdi-menu-down btn"></button>
    </div>
    <div class="preview-cards">
      <div class="side-card-wrapper left">
        <project-preview @click="scrollAvant"
                         :model="precedent"
                         class="back-cards"
        />
      </div>
      
      <project-preview
          :model="courant"
          :can-redirect="true"
          class="front-card"
      />

      <div class="side-card-wrapper right">
        <project-preview @click="scrollApres"
                         :model="suivant"
                         class="back-cards"
        />
      </div>
    </div>
    <div v-if="!isMobile" class="dots">
      <span v-for="(item, dotIndex) in list" :key="dotIndex" :class="getDotClass(dotIndex)"
            @click="scrollToIndex(dotIndex)"></span>
    </div>
  </div>
</template>

<style scoped>
.list-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  position: relative;
  overflow: hidden;
}

.preview-cards {
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  height: 80%;
  width: 100%;
  position: relative;
}

.front-card {
  width: 60%;
  height: 90%;
  transform: scale(1);
  box-shadow: 0 8px 32px rgba(19, 124, 139, 0.3);
  z-index: 10;
  transition: all 0.4s ease;
  overflow-y: hidden;
  flex-shrink: 0;
}

.side-card-wrapper {
  position: absolute;
  width: 40%;
  height: 90%;
  display: flex;
  align-items: center;
  z-index: 5;
  transition: all 0.4s ease;
}

.side-card-wrapper.left {
  left: -20%; /* Plus proche du centre */
  justify-content: flex-end;
}

.side-card-wrapper.right {
  right: -20%; /* Plus proche du centre */
  justify-content: flex-start;
}

.back-cards {
  width: 100%;
  height: 80%;
  opacity: 0.4;
  transform: scale(0.85);
  transition: all 0.4s ease;
  cursor: pointer;
}

.side-card-wrapper:hover .back-cards {
  opacity: 0.7;
  transform: scale(0.9);
}

.front-card:hover {
  box-shadow: 0 12px 48px rgba(19, 124, 139, 0.4);
  transform: scale(1.02);
}

/* Effet fade sur les côtés pour les cartes latérales */
.side-card-wrapper::after {
  content: '';
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 20;
}

.side-card-wrapper.left::after {
  background: linear-gradient(to right, var(--background-color) 20%, transparent 100%);
}

.side-card-wrapper.right::after {
  background: linear-gradient(to left, var(--background-color) 20%, transparent 100%);
}

/* Boutons pour mobile */
.nav-btn {
  display: flex;
  justify-content: center;
  flex-direction: column;
  position: absolute;
  right: 4vw;
  z-index: 100;
}

.btn {
  display: block;
  border-radius: 50%;
  border: 2px solid var(--primary-color);
  background-color: var(--card-background);
  color: var(--text-color);
  width: 50px;
  height: 50px;
  margin: 10px 0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn:hover {
  background-color: var(--primary-color);
  transform: scale(1.1);
}

/* Dots styles */
.dots {
  display: flex;
  flex-direction: row;
  align-items: center;
  position: absolute;
  bottom: 8vh;
  left: 50%;
  transform: translateX(-50%);
  gap: 8px;
}

.dot {
  width: 12px;
  height: 12px;
  background-color: var(--dark-border);
  border-radius: 50%;
  cursor: pointer;
  opacity: 0.5;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.dot:hover {
  opacity: 0.8;
  transform: scale(1.1);
}

.dot.adjacent {
  opacity: 0.75;
  background-color: var(--secondary-color);
}

.dot.active {
  width: 40px;
  height: 12px;
  border-radius: 6px;
  opacity: 1;
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}
</style>